import React, { useEffect, useCallback } from "react";
import { useStateIfMounted } from "use-state-if-mounted";
import { useDispatch, useSelector } from "react-redux";
import { GridTable, Breadcrumb, ButtonMenu, DialogModal, TopButtons } from "../../components";
import { organizationActions } from "../../redux/actions/guides";
import SelectFilter from "@inovua/reactdatagrid-community/SelectFilter";
import NumberFilter from "@inovua/reactdatagrid-community/NumberFilter";
import { IconButton, Card, MenuItem, Icon, Grid, Link } from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { ModalOrganization } from "./Modals";
import useToggle from "../../hooks/useToggle";
import { useDocTitle } from "../../hooks/useDocTitle";
import { hasPermission, MenuSection, OperationType } from "../../auth/authRoles";
import useAuth from "../../hooks/useAuth";
import { useGridSettings } from "../../hooks/useGridSettings";
import { useUserFilter } from "../../hooks/useUserFilter";

const filterValue = [
  {
    name: "index",
    operator: "eq",
    type: "number",
    emptyValue: "",
    filterEditorProps: { stepOnWheel: false, minValue: 0 },
  },
  {
    name: "id",
    operator: "eq",
    type: "number",
    emptyValue: "",
    filterEditorProps: { stepOnWheel: false, minValue: 0 },
  },
  { name: "inn", operator: "contains", type: "string", value: "" },
  { name: "nameOrganization", operator: "contains", type: "string", value: "" },
  { name: "phone", operator: "startsWith", type: "string", value: "" },
  { name: "email", operator: "startsWith", type: "string", value: "" },
  { name: "address", operator: "contains", type: "string", value: "" },
  { name: "isArchive", operator: "inlist", type: "select", value: [false], },
];

const Organization = () => {
  const dispatch = useDispatch();

  const { user } = useAuth();

  useDocTitle("Справочник организаций");

  const docType = MenuSection.COMMON;

  const organizations = useSelector((state) => state.guides.organizations.items);
  const loading = useSelector((state) => state.guides.organizations.loadingItems);

  const [selectedRow, setSelectedRow] = useStateIfMounted(null);
  const [isAddMode, setIsAddMode] = useStateIfMounted(true);

  const [show, setShow] = useToggle();
  const [dialog, setDialog] = useToggle();
  const [filter, toggleFilter] = useUserFilter();
  const [update, setUpdate] = useToggle();

  const TABLE_KEY = 'guides:organizations';

  const {
    applyToColumns,
    onVisibilityChange,
    onResize,
    onColumnReorder,
    isLoading: settingsLoading
  } = useGridSettings(TABLE_KEY);

  const columns = [
    {
      name: "index",
      header: "№",
      minWidth: 100,
      defaultFlex: 0.2,
      hideable: false,
      type: 'number',
      filterEditor: NumberFilter,
    },
    {
      name: "isArchive",
      header: "Статус",
      minWidth: 150,
      defaultFlex: 0.1,
      defaultVisible: false,
      textAlign: "center",
      filterEditor: SelectFilter,
      filterEditorProps: {
        multiple: true,
        wrapMultiple: false,
        listEmptyText: "Нет данных",
        dataSource: [
          { id: true, label: "Архивный" },
          { id: false, label: "Активный" },
        ],
      },
      render: ({ value }) => (value === true ? <Icon color="secondary"> archive </Icon> : <Icon style={{ color: green[500] }}> done_all </Icon>),
    },
    {
      name: "id",
      header: "ID",
      minWidth: 100,
      defaultFlex: 0.2,
      defaultVisible: false,
      type: 'number',
      filterEditor: NumberFilter,
    },
    { name: "inn", header: "ИНН", minWidth: 150, defaultFlex: 0.5 },
    { name: "nameOrganization", header: "Наименование", minWidth: 150, defaultFlex: 0.7 },
    {
      name: "phone",
      header: "Телефон",
      minWidth: 150,
      defaultFlex: 0.5,
      render: ({ value }) => value && <Link href={`tel:${value}`}>{value}</Link>,
    },
    {
      name: "email",
      header: "Email",
      minWidth: 150,
      defaultFlex: 0.5,
      render: ({ value }) => value && <Link href={`mailto:${value}`}>{value}</Link>,
    },
    { name: "address", header: "Фактический адрес", minWidth: 150, defaultFlex: 1 },
    {
      name: "actions",
      header: "",
      minWidth: 70,
      defaultFlex: 0.1,
      defaultVisible: true,
      showColumnMenuTool: false,
      headerAlign: "center",
      textAlign: "center",
      render: ({ data }) => (
        <ButtonMenu
          menuButton={
            <IconButton>
              <Icon style={{ color: green[500] }}> info </Icon>
            </IconButton>
          }
        >
          <MenuItem onClick={() => ArchiveClick(data?.id)}>
            <Icon color="secondary"> {data?.isArchive === true ? "unarchive" : "archive"} </Icon>
            <span className="pl-4"> {data?.isArchive === true ? "Восстановить" : "В архив"} </span>
          </MenuItem>
          {hasPermission(docType, OperationType.EDIT, user) && (
            <MenuItem onClick={() => setDialog()}>
              <Icon color="error"> delete </Icon>
              <span className="pl-4"> Удалить </span>
            </MenuItem>
          )}
        </ButtonMenu>
      ),
    },
  ];

  const RowClick = useCallback((event, cellProps) => {
    const { name } = cellProps;

    setSelectedRow(cellProps.data);

    if (name === "actions" || name === "phone" || name === "email") return;

    setIsAddMode(false);
    setShow();
  }, []);

  const AddClick = () => {
    setIsAddMode(true);
    setShow();
  };

  const ArchiveClick = useCallback((id) => {
    setIsAddMode(true);
    dispatch(organizationActions.archive(id));
  }, []);

  const DeleteClick = useCallback((id) => {
    setIsAddMode(true);
    dispatch(organizationActions.delete(id));
    setDialog();
  }, []);

  useEffect(() => {
    dispatch(organizationActions.getAll());
  }, [update]);

  return (
    <div className="p-sm-30 flex flex-column h-full">
      <Grid container spacing={1} className="mb-4">
        <Grid item xs={12} lg={6}>
          <Breadcrumb routeSegments={[{ name: "Справочники", path: "/guides/organizations" }, { name: "Организации" }]} />
        </Grid>
        <Grid item xs={12} lg={6} className="mt-sm-10">
          <TopButtons addClick={() => AddClick()} updateClick={() => setUpdate()} filterClick={toggleFilter} text="Организация"
            winPermissionName={docType} />
        </Grid>
      </Grid>

      <Card className="p-1 flex-grow">
        <GridTable
          items={organizations}
          fields={settingsLoading ? columns : applyToColumns(columns)}
          filtering={filter}
          loading={loading}
          filter={filterValue}
          onRowClick={RowClick}
          onColumnVisibleChange={(payload) => {
            const column = payload?.[0] || payload;
            if (column?.column?.name) {
              onVisibilityChange({ name: column.column.name, visible: column.visible });
            }
          }}
          onColumnOrderChange={(columnNames) => {
            if (Array.isArray(columnNames)) {
              onColumnReorder(columnNames.filter(Boolean), columns);
            }
          }}
        />
      </Card>

      <ModalOrganization modalShow={show} onClose={() => setShow()} isAddMode={isAddMode} setIsAddMode={setIsAddMode} id={selectedRow?.id} />

      <DialogModal
        open={dialog}
        onConfirmDialogClose={() => setDialog()}
        onYesClick={() => DeleteClick(selectedRow?.id)}
        text={`Вы действительно хотите удалить организацию - ${selectedRow?.nameOrganization}? Это действие невозможно отменить.`}
      />
    </div>
  );
};

export default Organization;
